import React, { useCallback, useEffect, useState, useContext, useRef } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import FileUploadRoundedIcon from '@mui/icons-material/FileUploadRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import StudentLayout from './components/StudentLayout';
import { AuthContext } from '../auth/AuthContext';
import axios from '../api/api';
import { useNavigate } from 'react-router-dom';

const cleanText = (value) => (typeof value === 'string' ? value.trim() : '');
const uniqueItems = (arr) => Array.from(new Set(arr));
const splitListInput = (value) => {
  if (Array.isArray(value)) return uniqueItems(value.map(cleanText).filter(Boolean));
  if (typeof value !== 'string') return [];
  return uniqueItems(
    value
      .split(/[\s,;]+/)
      .map((item) => item.trim())
      .filter(Boolean)
  );
};
const listToInput = (list) => {
  if (Array.isArray(list)) return list.join(', ');
  if (typeof list === 'string') return list.trim();
  return '';
};

const emptyStudentProfile = {
  name: '',
  email: '',
  phone: '',
  country: '',
  province: '',
  town: '',
  suburb: '',
  curricula: '',
  gradeLevels: '',
  subjectFocus: '',
  tutoringOptions: [],
};

const MAX_IMAGE_MB = 2;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const TUTORING_OPTIONS = [
  { value: 'online', label: 'Online' },
  { value: 'in-person', label: 'In-person' },
];

const canonicalBust = (url, updatedAt) => {
  if (!url) return null;
 const v = updatedAt ? new Date(updatedAt).getTime() : Date.now();
  return `${url.split('?')[0]}?v=${v}`;
};

const StudentSettings = () => {
  const { token, user, setUser } = useContext(AuthContext) ?? {};
  const navigate = useNavigate();

  const [profile, setProfile] = useState(emptyStudentProfile);
  const [profilePicture, setProfilePicture] = useState(null);
  const [preview, setPreview] = useState(null);
  const [passwords, setPasswords] = useState({ current: '', new: '', confirm: '' });
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [loadingPassword, setLoadingPassword] = useState(false);
  const [profileMessage, setProfileMessage] = useState(null);
  const [passwordMessage, setPasswordMessage] = useState(null);

  const lastObjectUrlRef = useRef(null);

  const revokeObjectUrl = useCallback(() => {
    if (lastObjectUrlRef.current) {
      try {
        URL.revokeObjectURL(lastObjectUrlRef.current);
      } catch (error) {
        console.debug('[StudentSettings] revokeObjectURL error:', error);
      }
      lastObjectUrlRef.current = null;
    }
  }, []);

  const loadProfile = useCallback(async () => {
    if (!token || !user?._id) return null;
    const { data } = await axios.get(`/users/${user._id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const { name, email, phone, profilePictureUrl, updatedAt } = data || {};
    setProfile({
      name: name || '',
      email: email || '',
      phone: phone || '',
      country: data?.country || '',
      province: data?.province || '',
      town: data?.town || '',
      suburb: data?.suburb || '',
      curricula: listToInput(data?.curricula),
      gradeLevels: listToInput(data?.gradeLevels),
      subjectFocus: listToInput(data?.subjectFocus),
      tutoringOptions: Array.isArray(data?.tutoringOptions) ? data.tutoringOptions : [],
    });
    setPreview(canonicalBust(profilePictureUrl || null, updatedAt));
    return data;
  }, [token, user?._id]);

  useEffect(() => {
    if (!token || !user?._id) {
      navigate('/login', { replace: true });
      return;
    }
    let isMounted = true;
   (async () => {
      setLoadingProfile(true);
      setProfileMessage(null);
      try {
        if (isMounted) await loadProfile();
      } catch (err) {
        console.error('[StudentSettings] Failed to load profile:', err);
        if (isMounted) setProfileMessage({ type: 'error', text: 'Failed to load profile.' });
      } finally {
        if (isMounted) setLoadingProfile(false);
      }
    })();
    return () => {
      isMounted = false;
      revokeObjectUrl();
    };
  }, [token, user?._id, navigate, loadProfile, revokeObjectUrl]);

  const handleProfileChange = (event) => {
    const { name, value } = event.target;
    setProfile((prev) => ({ ...prev, [name]: value }));
  };

  const toggleTutoringOption = (option) => {
    setProfile((prev) => {
 const current = Array.isArray(prev.tutoringOptions) ? prev.tutoringOptions : [];
      const next = new Set(current);
 next.has(option) ? next.delete(option) : next.add(option);
      return { ...prev, tutoringOptions: Array.from(next) };
    });
  };

  const handleProfilePictureChange = (event) => {
    const file = event.target.files?.[0];
    setProfileMessage(null);
    setProfilePicture(null);
    revokeObjectUrl();
    if (!file) return;

    if (!ALLOWED_TYPES.includes(file.type)) {
      setProfileMessage({ type: 'error', text: 'Please upload a JPG, PNG, WEBP or GIF image.' });
      return;
    }
    if (file.size > MAX_IMAGE_MB * 1024 * 1024) {
      setProfileMessage({ type: 'error', text: `Image must be ${MAX_IMAGE_MB}MB or smaller.` });
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    lastObjectUrlRef.current = objectUrl;
    setProfilePicture(file);
    setPreview(objectUrl);
  };

  const validateProfile = () => {
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profile.email);
    const phoneOk = !profile.phone || /^\+?[0-9\s-]{7,15}$/.test(profile.phone);
    if (!profile.name.trim()) {
      setProfileMessage({ type: 'error', text: 'Name is required.' });
      return false;
    }
    if (!emailOk) {
      setProfileMessage({ type: 'error', text: 'Please enter a valid email address.' });
      return false;
    }
    if (!phoneOk) {
      setProfileMessage({
        type: 'error',
        text: 'Please enter a valid phone number (7-15 digits, may include spaces, dashes, optional +).',
      });
      return false;
    }
    return true;
  };

  const updateProfile = async (event) => {
    event.preventDefault();
    setProfileMessage(null);
    if (!validateProfile()) return;

    try {
      setLoadingProfile(true);
      const payload = {
        name: cleanText(profile.name),
        email: cleanText(profile.email),
        phone: cleanText(profile.phone),
        country: cleanText(profile.country),
        province: cleanText(profile.province),
        town: cleanText(profile.town),
        suburb: cleanText(profile.suburb),
        curricula: splitListInput(profile.curricula),
        gradeLevels: splitListInput(profile.gradeLevels),
        subjectFocus: splitListInput(profile.subjectFocus),
 tutoringOptions: Array.isArray(profile.tutoringOptions) ? profile.tutoringOptions : [],
      };

      if (profilePicture) {
        const fd = new FormData();
        fd.append('name', payload.name);
        fd.append('email', payload.email);
        fd.append('phone', payload.phone);
        fd.append('country', payload.country);
        fd.append('province', payload.province);
        fd.append('town', payload.town);
        fd.append('suburb', payload.suburb);
        fd.append('curricula', JSON.stringify(payload.curricula));
        fd.append('gradeLevels', JSON.stringify(payload.gradeLevels));
        fd.append('subjectFocus', JSON.stringify(payload.subjectFocus));
        fd.append('tutoringOptions', JSON.stringify(payload.tutoringOptions));
        fd.append('profilePicture', profilePicture);
        await axios.put(`/users/${user._id}`, fd, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        });
      } else {
        await axios.put(`/users/${user._id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }

      const fresh = await loadProfile();
      if (fresh && typeof setUser === 'function') {
        setUser((prev) => ({ ...(prev || {}), ...fresh }));
        try {
          localStorage.setItem('user', JSON.stringify({ ...(user || {}), ...fresh }));
        } catch {}
      }

      setProfilePicture(null);
      revokeObjectUrl();
      setProfileMessage({ type: 'success', text: 'Profile updated successfully.' });
    } catch (err) {
      console.error('Failed to update profile:', err);
      setProfileMessage({
        type: 'error',
        text: err?.response?.data?.message || 'Failed to update profile. Please try again.',
      });
    } finally {
      setLoadingProfile(false);
    }
  };

  const updatePassword = async (event) => {
    event.preventDefault();
    setPasswordMessage(null);
    if (!passwords.current || !passwords.new || !passwords.confirm) {
      setPasswordMessage({ type: 'error', text: 'Please fill in all password fields.' });
      return;
    }
    if (passwords.new.length < 8) {
      setPasswordMessage({ type: 'error', text: 'New password must be at least 8 characters.' });
      return;
    }
    if (passwords.new !== passwords.confirm) {
      setPasswordMessage({ type: 'error', text: 'New passwords do not match.' });
      return;
    }

    try {
      setLoadingPassword(true);
      await axios.post(
        `/users/${user._id}/change-password`,
        { currentPassword: passwords.current, newPassword: passwords.new },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPasswordMessage({ type: 'success', text: 'Password updated successfully.' });
      setPasswords({ current: '', new: '', confirm: '' });
    } catch (err) {
      console.error('Failed to update password:', err);
      setPasswordMessage({
        type: 'error',
        text: err?.response?.data?.message || 'Failed to update password. Please try again.',
      });
    } finally {
      setLoadingPassword(false);
    }
  };

  const profileSeverity = profileMessage?.type === 'error' ? 'error' : 'success';
  const passwordSeverity = passwordMessage?.type === 'error' ? 'error' : 'success';

  return (
    <StudentLayout>
      <Box>
        <Stack spacing={3}>
          <Card variant="outlined">
            <CardHeader
              avatar={
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <PersonRoundedIcon />
                </Avatar>
              }
              title={
                <Typography variant="h5" fontWeight={800} color="primary.main">
                  Student account settings
                </Typography>
              }
              subheader="Update your profile details and let us know how to reach you."
              subheaderTypographyProps={{ sx: { color: 'text.secondary' } }}
            />
            <Divider />
            <CardContent>
              <Box component="form" onSubmit={updateProfile} noValidate>
                <Stack spacing={3}>
                  {profileMessage && (
                    <Alert severity={profileSeverity}>{profileMessage.text}</Alert>
                  )}

                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={3}
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                  >
                    <Avatar
                      src={preview || undefined}
                      alt={profile.name || 'Profile preview'}
                      sx={{ width: 96, height: 96 }}
                    >
                      {profile.name?.charAt(0)?.toUpperCase() || 'S'}
                    </Avatar>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                      <Button
                        component="label"
                        variant="outlined"
                        startIcon={<FileUploadRoundedIcon />}
                        disabled={loadingProfile}
                      >
                        Upload photo
                        <input
                          hidden
                          accept={ALLOWED_TYPES.join(',')}
                          type="file"
                          onChange={handleProfilePictureChange}
                        />
                      </Button>
                      {preview && !preview.startsWith('http') && (
                        <Button
                          variant="outlined"
                          color="error"
                          startIcon={<DeleteOutlineRoundedIcon />}
                          onClick={() => {
                            setProfilePicture(null);
                            revokeObjectUrl();
                            setPreview(null);
                          }}
                          disabled={loadingProfile}
                        >
                          Clear
                        </Button>
                      )}
                    </Stack>
                  </Stack>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Full name"
                        name="name"
                        value={profile.name}
                        onChange={handleProfileChange}
                        fullWidth
                        required
                        disabled={loadingProfile}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Email"
                        name="email"
                        type="email"
                        value={profile.email}
                        onChange={handleProfileChange}
                        fullWidth
                        required
                        disabled={loadingProfile}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Phone number"
                        name="phone"
                        value={profile.phone}
                        onChange={handleProfileChange}
                        fullWidth
                        disabled={loadingProfile}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Country"
                        name="country"
                        value={profile.country}
                        onChange={handleProfileChange}
                        fullWidth
                        disabled={loadingProfile}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Province / State"
                        name="province"
                        value={profile.province}
                        onChange={handleProfileChange}
                        fullWidth
                        disabled={loadingProfile}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Town or city"
                        name="town"
                        value={profile.town}
                        onChange={handleProfileChange}
                        fullWidth
                        disabled={loadingProfile}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Suburb"
                        name="suburb"
                        value={profile.suburb}
                        onChange={handleProfileChange}
                        fullWidth
                        disabled={loadingProfile}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Curricula (comma separated)"
                        name="curricula"
                        value={profile.curricula}
                        onChange={handleProfileChange}
                        fullWidth
                        multiline
                        minRows={2}
                        disabled={loadingProfile}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Grades (comma separated)"
                        name="gradeLevels"
                        value={profile.gradeLevels}
                        onChange={handleProfileChange}
                        fullWidth
                        multiline
                        minRows={2}
                        disabled={loadingProfile}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Subjects (comma separated)"
                        name="subjectFocus"
                        value={profile.subjectFocus}
                        onChange={handleProfileChange}
                        fullWidth
                        multiline
                        minRows={2}
                        disabled={loadingProfile}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }} color="primary.main">
                        Tutoring options
                      </Typography>
                      <FormGroup row>
                        {TUTORING_OPTIONS.map((option) => (
                          <FormControlLabel
                            key={option.value}
                            control={
                              <Checkbox
                                checked={
                                  Array.isArray(profile.tutoringOptions) &&
                                  profile.tutoringOptions.includes(option.value)
                                }
                                onChange={() => toggleTutoringOption(option.value)}
                                disabled={loadingProfile}
                              />
                            }
                            label={option.label}
                          />
                        ))}
                      </FormGroup>
                    </Grid>
                  </Grid>

                  <Stack direction="row" justifyContent="flex-end">
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={
 loadingProfile ? <CircularProgress size={18} color="inherit" /> : <SaveRoundedIcon />
                      }
                      disabled={loadingProfile}
                    >
 {loadingProfile ? 'Saving...' : 'Save changes'}
                    </Button>
                  </Stack>
                </Stack>
              </Box>
            </CardContent>
          </Card>

          <Card variant="outlined">
            <CardHeader
              avatar={
                <Avatar sx={{ bgcolor: 'secondary.main' }}>
                  <LockRoundedIcon />
                </Avatar>
              }
              title={
                <Typography variant="h6" fontWeight={700} color="primary.main">
                  Change password
                </Typography>
              }
              subheader="Keep your account secure with a strong password."
              subheaderTypographyProps={{ sx: { color: 'text.secondary' } }}
            />
            <Divider />
            <CardContent>
              <Box component="form" onSubmit={updatePassword} noValidate>
                <Stack spacing={2}>
                  {passwordMessage && (
                    <Alert severity={passwordSeverity}>{passwordMessage.text}</Alert>
                  )}
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Current password"
                        type="password"
                        name="current"
                        value={passwords.current}
                        onChange={(event) =>
                          setPasswords((prev) => ({ ...prev, current: event.target.value }))
                        }
                        fullWidth
                        required
                        disabled={loadingPassword}
                        autoComplete="current-password"
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="New password"
                        type="password"
                        name="new"
                        value={passwords.new}
                        onChange={(event) =>
                          setPasswords((prev) => ({ ...prev, new: event.target.value }))
                        }
                        fullWidth
                        required
                        disabled={loadingPassword}
                        autoComplete="new-password"
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Confirm new password"
                        type="password"
                        name="confirm"
                        value={passwords.confirm}
                        onChange={(event) =>
                          setPasswords((prev) => ({ ...prev, confirm: event.target.value }))
                        }
                        fullWidth
                        required
                        disabled={loadingPassword}
                        autoComplete="new-password"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Stack direction="row" justifyContent="flex-end">
                        <Button
                          type="submit"
                          variant="contained"
                          startIcon={
                            loadingPassword ? (
                              <CircularProgress size={18} color="inherit" />
                            ) : (
                              <SaveRoundedIcon />
                            )
                          }
                          disabled={loadingPassword}
                        >
                          {loadingPassword ? 'Updating...' : 'Update password'}
                        </Button>
                      </Stack>
                    </Grid>
                  </Grid>
                </Stack>
              </Box>
            </CardContent>
          </Card>
        </Stack>
      </Box>
    </StudentLayout>
  );
};

export default StudentSettings;
